import React from 'react';
import SidebarParametres from '../../../layout/SidebarParametres';
import { useOAD } from '../../../layout/OADLayout';
import { api } from '../../shared/api/api';
import { useApiData } from '../../shared/hooks/useFetch';
import LimiteErreur from '../../shared/components/LimiteErreur';
import { RCP_OPTIONS } from '../../shared/constants';
import { useHorizon } from '../../shared/hooks/useHorizon';
import { libelleHorizon } from '../../shared/utils/horizon';
import ClassementAdaptationsChart, {
  couleursParSolution,
} from '../../shared/charts/ClassementAdaptationsChart';

/**
 * Clé de l'horizon dans la réponse de l'API classement. En TRACC elle écrit
 * "reference", "+2" et "+2.7" (comme les autres routes), alors que le select
 * de la sidebar manipule "reference", "2" et "2.7".
 */
function cleReponse(rcp, annee) {
  return rcp === 'tracc' && annee !== 'reference' ? `+${annee}` : String(annee);
}

/** Liste des solutions d'un horizon dans la réponse (tableau vide si absente). */
function listePour(data, rcp, horizon) {
  return data?.[cleReponse(rcp, horizon)] ?? data?.[horizon] ?? [];
}

/** Adaptations / Classement des solutions. */
export default function Classement() {
  const { idExploit } = useOAD();
  const [rcp, setRcp] = React.useState(RCP_OPTIONS[0].value);
  // En TRACC, l'année (2035/2050) n'a pas de sens : on bascule sur les
  // niveaux de réchauffement (+2°C / +2.7°C), cf. shared/utils/horizon.js.
  // (horizonsSelectionnables ne teste que scenario === 'tracc', et rcp vaut
  // déjà 'tracc' tel quel dans RCP_OPTIONS.)
  const [annee, setAnnee, anneesPossibles] = useHorizon(rcp, {
    // 2020 (ou la référence en TRACC) proposée en plus de 2035/2050 ; 2035 (ou
    // +2°C) reste coché à l'ouverture.
    avecReference: true,
    indexParDefaut: 1,
  });

  // Pas d'année dans la requête : la réponse contient directement une liste
  // par horizon ({ 2020: [...], 2035: [...], 2050: [...] }, ou reference / +2 /
  // +2.7 en TRACC) et la page choisit celle de la sidebar. Changer d'année ne
  // relance donc pas d'appel.
  const parametres = React.useMemo(
    () => ({ idExploitation: idExploit, rcp }),
    [idExploit, rcp]
  );
  const { data, loading, error } = useApiData(api.getClassementSolutions, parametres);

  // Solutions qui ne changent pas de carré de couleur entre 2035 et 2050 (ou
  // +2 °C et +2.7 °C en TRACC) : leur ligne est entourée de noir dans le
  // tableau, quelle que soit l'année affichée. On compare la couleur de la
  // ligne dans chacun des deux graphiques (blanc compris).
  const solutionsStables = React.useMemo(() => {
    const [h1, h2] = rcp === 'tracc' ? ['2', '2.7'] : ['2035', '2050'];
    const couleurs1 = couleursParSolution(listePour(data, rcp, h1));
    const couleurs2 = couleursParSolution(listePour(data, rcp, h2));
    return [...couleurs1.keys()].filter(
      (nom) => couleurs2.has(nom) && couleurs1.get(nom) === couleurs2.get(nom)
    );
  }, [data, rcp]);

  return (
    <div className="oad-contenu">
      <SidebarParametres
        niveau1="2035-2050"
        niveau2="adaptations"
        niveau3="classement"
        idExploit={idExploit}
      >
        <label className="sidebar-champ">
          <span className="sidebar-label">{rcp === 'tracc' ? 'Réchauffement' : 'Année'}</span>
          <select value={annee} onChange={(e) => setAnnee(e.target.value)}>
            {anneesPossibles.map((h) => (
              <option key={h} value={h}>{libelleHorizon(rcp, h)}</option>
            ))}
          </select>
        </label>

        <fieldset className="sidebar-champ sidebar-groupe">
          <legend className="sidebar-label">Scénarios climatiques</legend>
          {RCP_OPTIONS.map((option) => (
            <label key={option.value} className="sidebar-radio">
              <input
                type="radio"
                name="scenario-clim-classement"
                value={option.value}
                checked={rcp === option.value}
                onChange={(e) => setRcp(e.target.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </fieldset>
      </SidebarParametres>

      <main className="oad-cards">
        <section className="oad-card">
          <h2 className="tdb-section-titre">Classement des solutions d'adaptation</h2>
          {error && <p className="tdb-erreur">{error}</p>}
          {loading ? (
            <p className="tdb-chargement">Chargement…</p>
          ) : (
            !error && (
              <LimiteErreur cle={`${annee}-${rcp}`}>
                <ClassementAdaptationsChart
                  data={listePour(data, rcp, annee)}
                  solutionsStables={solutionsStables}
                />
              </LimiteErreur>
            )
          )}
        </section>
      </main>
    </div>
  );
}
